'use client';

import { ArrowDown } from '@/assets/icons/ArrowDown';
import { useOutsideClick } from '@/hooks';
import { FormData, USStates } from '@/types/form';
import { stateAbbreviations } from '@/utils/form';
import { RefObject, useEffect, useState } from 'react';
import {
  FieldError,
  UseFormRegisterReturn,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';
import styles from './StateSelect.module.scss';

interface Props {
  placeholder: string;
  label: string;
  id: string;
  error?: FieldError;
  register: UseFormRegisterReturn;
  setValue?: UseFormSetValue<FormData>;
  trigger?: UseFormTrigger<FormData>;
  formData?: FormData;
}

const StateSelect = ({
  placeholder,
  label,
  id,
  error,
  register,
  setValue,
  trigger,
  formData,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>(
    formData?.state || ''
  );
  const sortRef = useOutsideClick(() => setIsOpen(false));

  useEffect(() => {
    if (selectedValue) {
      setValue?.('state', selectedValue as USStates);
      trigger?.('state');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  const filteredStates = stateAbbreviations.filter((state) =>
    state.toLowerCase().includes(selectedValue.toLowerCase())
  );

  const handleOptionClick = (value: string) => {
    setSelectedValue(value);
    setIsOpen(false);
  };

  return (
    <div className={`${styles.container} ${error ? styles.errorField : ''}`}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <input id={id} type="text" className={styles.hiddenInput} {...register} />
      <div
        className={`${styles.wrapper} ${isOpen ? styles.toggledWrapper : ''}`}
      >
        <input
          placeholder={placeholder}
          value={selectedValue}
          maxLength={2}
          onChange={(e) => {
            setSelectedValue(e.target.value);
            setIsOpen(true);
          }}
          className={styles.input}
        />
        <span
          onClick={() => setIsOpen((prev) => !prev)}
          className={`${styles.iconWrapper} ${isOpen ? styles.openIcon : ''}`}
        >
          <ArrowDown />
        </span>

        {isOpen && (
          <ul
            ref={sortRef as RefObject<HTMLUListElement>}
            className={styles.optionsWrapper}
          >
            {filteredStates.length > 0 ? (
              filteredStates.map((value) => (
                <li
                  onClick={() => handleOptionClick(value)}
                  className={styles.option}
                  key={value}
                >
                  {value}
                </li>
              ))
            ) : (
              <li className={styles.option}>No matching states</li>
            )}
          </ul>
        )}
      </div>
      {error && <p className={styles.error}>{error.message}</p>}
    </div>
  );
};

export default StateSelect;
